/**
 * Runtime assertion that a relation is loaded (not undefined).
 * Throws if relation was not included in query.
 */
export function assertRelationLoaded<T, K extends keyof T>(
    entity: T,
    relation: K,
    message?: string,
): asserts entity is T & Required<Pick<T, K>> {
    if ((entity as any)[relation] === undefined) {
        throw new Error(
            message ??
                `Relation "${String(relation)}" was not loaded. ` +
                    `Include it in the relations option when querying.`,
        );
    }
}

/**
 * Type guard for checking if an entity has a loaded user relation.
 * Narrows the type from `user?: User | null` to `user: User | null`.
 *
 * @example
 * if (hasLoadedUser(entity)) {
 *     // entity.user is now typed as User | null (not undefined)
 * }
 */
export function hasLoadedUser<T extends { user?: unknown }>(
    entity: T,
): entity is T & { user: NonNullable<T["user"]> | null } {
    return entity.user !== undefined;
}

/**
 * Type guard for checking if any relation is loaded (not undefined).
 * Generic version of hasLoadedUser.
 *
 * @example
 * if (hasLoadedRelation(transaction, 'entity')) {
 *     // transaction.entity is now typed without undefined
 * }
 */
export function hasLoadedRelation<T, K extends keyof T>(
    obj: T,
    key: K,
): obj is T & { [P in K]-?: T[P] } {
    return obj[key] !== undefined;
}

/**
 * Asserts that specified relations are loaded and returns the entity cast to the strict type.
 * Use after loading relations via query or repository methods.
 *
 * @example
 * const entity = await repo.findOne({ where: { id }, relations: { user: true } });
 * const strictEntity = asLoaded(entity, ['user']); // Returns Entity (strict) instead of EntityBase
 */
export function asLoaded<T, R extends T>(entity: T, relations: string[]): R {
    for (const relation of relations) {
        assertRelationLoaded(entity, relation as keyof T);
    }
    return entity as R;
}
